import React, { useState } from "react";
import { Typography } from "@material-ui/core";
import styled, { css } from "styled-components";
import { brandPalette } from "brand/theme/brandTheme";
import memberLoginImage from "../../../../../assets/Member-login-Registration.png";
import connectImage from "../../../../../assets/SAICE-Connect.png";
import educationImage from "../../../../../assets/SAICE-EducationTraining.png";
import bookshopImage from "../../../../../assets/SAICE-Bookshop.png";
import memberLoginImageGold from "../../../../../assets/Member-login-Registration-gold.png";
import connectImageGold from "../../../../../assets/SAICE-Connect-gold.png";
import educationImageGold from "../../../../../assets/SAICE-Education-Training-gold.png";
import bookshopImageGold from "../../../../../assets/SAICE-Bookstore-gold.png";

const IconBoxContainer = styled.div(
  (props) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: ${props.useImage ? 'transparent' : brandPalette.Secondary[900]};
    padding: ${props.useImage ? '0' : '2rem 1.5rem'};
    min-width: ${props.useImage ? 'auto' : '200px'};
    min-height: ${props.useImage ? 'auto' : '250px'};
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: ${props.useImage ? 'visible' : 'hidden'};

    &:hover {
      background-color: ${props.useImage ? 'transparent' : brandPalette.Secondary[700]};
      transform: ${props.useImage ? 'none' : 'translateY(-5px)'};
      box-shadow: ${props.useImage ? 'none' : '0 10px 25px rgba(0, 0, 0, 0.3)'};
    }

    &:hover .icon-circle {
      background-color: ${brandPalette.Secondary[600]};
      transform: scale(1.1);
    }

    &:hover .icon-image {
      transform: scale(1.1);
    }
  `
);

const IconCircle = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background-color: ${brandPalette.Secondary[800]};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
`;

const IconImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  transition: all 0.3s ease;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 300px;
  height: 300px;
  display: block;
  transition: all 0.3s ease;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 80%;
    background-color: #002441;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
    z-index: 1;
  }

  &:hover {
    img.regular {
      opacity: 0;
    }

    img.gold {
      opacity: 1;
    }

    &::after {
      opacity: 0.9;
    }

    .overlay-content {
      opacity: 1;
    }
  }
`;

const MemberLoginImage = styled.img`
  width: 300px;
  height: 300px;
  object-fit: contain;
  transition: opacity 0.3s ease;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  
  &.gold {
    opacity: 0;
  }
`;

const ImageIcon = styled.img`
  width: 300px;
  height: 300px;
  object-fit: contain;
  transition: opacity 0.3s ease;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  
  &.gold {
    opacity: 0;
  }
`;

const IconSVG = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  color: white;
  overflow: hidden;
  border-radius: 50%;
  
  svg {
    width: 100%;
    height: 100%;
    color: inherit;
  }
  
  img {
    width: 120%;
    height: 120%;
    object-fit: cover;
    object-position: center top;
  }
`;

const IconText = styled(Typography)`
  color: white;
  text-align: center;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.4;
  margin-top: 0.5rem;
`;

const OverlayContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  box-sizing: border-box;
  text-align: center;
  color: white;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 2;
`;

const OverlayTitle = styled(Typography)`
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 0.75rem;
`;

const OverlayText = styled(Typography)`
  font-size: 14px;
  line-height: 1.5;
`;

// Image-based icons with gold hover state
const MemberLoginIcon = () => {
  return (
    <ImageWrapper>
      <MemberLoginImage
        src={memberLoginImage}
        alt="Member login/ Registration"
        className="regular"
      />
      <MemberLoginImage
        src={memberLoginImageGold}
        alt="Member login/ Registration"
        className="gold"
      />
      <OverlayContent className="overlay-content">
        <OverlayText variant="body2">
          Access your SAICE member profile to manage personal details, memberships, and
          subscriptions. New users can register to unlock member-exclusive resources,
          services, and communications.
        </OverlayText>
      </OverlayContent>
    </ImageWrapper>
  );
};

const ConnectIcon = () => {
  return (
    <ImageWrapper>
      <ImageIcon
        src={connectImage}
        alt="SAICE Connect"
        className="regular"
      />
      <ImageIcon
        src={connectImageGold}
        alt="SAICE Connect"
        className="gold"
      />
      <OverlayContent className="overlay-content">
        <OverlayText variant="body2">
          Connect with industry professionals, employers, and opportunities through the
          SAICE networking platform.
        </OverlayText>
      </OverlayContent>
    </ImageWrapper>
  );
};

const BookshopIcon = () => {
  return (
    <ImageWrapper>
      <ImageIcon
        src={bookshopImage}
        alt="SAICE Bookshop"
        className="regular"
      />
      <ImageIcon
        src={bookshopImageGold}
        alt="SAICE Bookshop"
        className="gold"
      />
      <OverlayContent className="overlay-content">
        <OverlayTitle variant="subtitle1">
          Coming Soon
        </OverlayTitle>
        <OverlayText variant="body2">
          Browse and purchase technical publications, guidelines, standards, and industry
          references curated by SAICE. Access trusted resources that support professional
          practice and ongoing development.
        </OverlayText>
      </OverlayContent>
    </ImageWrapper>
  );
};

const EducationIcon = () => {
  return (
    <ImageWrapper>
      <ImageIcon
        src={educationImage}
        alt="SAICE Education/ Training"
        className="regular"
      />
      <ImageIcon
        src={educationImageGold}
        alt="SAICE Education/ Training"
        className="gold"
      />
      <OverlayContent className="overlay-content">
        <OverlayTitle variant="subtitle1">
          Coming Soon
        </OverlayTitle>
        <OverlayText variant="body2">
          Explore accredited courses, webinars, and training programmes designed to enhance
          technical competence and professional growth. Support your continuing professional
          development at every career stage.
        </OverlayText>
      </OverlayContent>
    </ImageWrapper>
  );
};

const iconComponents = {
  'member-login': MemberLoginIcon,
  'connect': ConnectIcon,
  'bookshop': BookshopIcon,
  'education': EducationIcon,
};

export default function IconBox({ iconSrc, iconType, title, onClick, linkUrl }) {
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (linkUrl) {
      window.location.href = linkUrl;
    }
  };

  const IconComponent = iconType && iconComponents[iconType];

  // Special handling for image-based icons - show image directly without background
  const imageBasedIcons = ['member-login', 'connect', 'bookshop', 'education'];
  const useImage = iconType && imageBasedIcons.includes(iconType);

  return (
    <IconBoxContainer onClick={handleClick} useImage={useImage}>
      {useImage ? (
        IconComponent && <IconComponent title={title} />
      ) : (
        <>
          <IconCircle className="icon-circle">
            {iconSrc ? (
              <IconImage src={iconSrc} alt={title} className="icon-image" onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }} />
            ) : null}
            {IconComponent ? (
              <IconSVG className="icon-image" style={{ display: iconSrc ? 'none' : 'flex' }}>
                <IconComponent />
              </IconSVG>
            ) : iconSrc ? null : (
              <IconSVG className="icon-image">
                <MemberLoginIcon />
              </IconSVG>
            )}
          </IconCircle>
          <IconText variant="body1">{title}</IconText>
        </>
      )}
    </IconBoxContainer>
  );
}

